/**
 * **鸣谢页（Credits）。**
 *
 * 评审：「在设置按钮之下，调试页按钮之上增加一个 credit 页，显示此项目使用的
 * 库（如 pixi）和代码（如 alex 的 shader）」。
 *
 * ## 为什么它是一屏，不是叠加层
 *
 * 它没有要改的**状态**，只有要读的一列字 —— 和设置那种"开着盖子改开关"的时序
 * 不是一回事。所以它走 `SCREEN`（推入/弹出），底部那颗按钮就是"返回"。
 *
 * ## 内容是**数据**
 *
 * 每一条鸣谢写成上面那个 `Credit` 数组里的一行。许可与出处都来自**源码文件
 * 头部的声明**（`render/bg/alexharri.ts`、`render/bg/glitch.ts`、
 * `audio/score.zenith.ts`）—— 这里只是把它们摆出来，不另立一份。
 * 依赖的版本/许可从 `package.json` 与各自的 `LICENSE` 读得。
 *
 * ⚠️ 这个文件只 import Pixi 的 `Container` 和 `render/ui` 控件 —— 它不碰
 * 游戏核心，所以永远不会因为规则改动而失效。
 */

import { Container } from 'pixi.js';
import {
  createColumn,
  richLabel,
  pageChrome,
  panel,
  scrollArea,
  sectionTitle,
  stampButton,
  TRACKING,
  uiPad,
  INK,
} from '../../render/ui';
import { bi, t, type TextKey } from '../../i18n';
import type { Screen, ScreenContext } from '../screen';

/** 面板宽度。比设置页宽一点：这里一行会写"谁 + 什么 + 什么许可"。 */
const PANEL_WIDTH = 760;
const PANEL_PADDING = 20;
/** 面板距页面顶部的距离（和设置页对齐，让开页眉行） */
const PANEL_TOP = 104;
/** 底部留给"返回"按钮的高度 */
const BUTTON_RESERVE = 72;
/** 滚动区内容左右各留的余量（遮罩裁掉按钮边的那种坑） */
const CONTENT_INSET = 8;

interface Credit {
  /** 库名或作品名 */
  readonly name: string;
  /** 它是什么、在这个项目里干什么 */
  readonly what: TextKey;
  /** 许可与出处 */
  readonly license: string;
}

const LIBRARIES: readonly Credit[] = [
  {
    name: 'pixi.js',
    what: 'credit.what.pixi',
    license: 'MIT',
  },
  {
    name: 'trystero',
    what: 'credit.what.trystero',
    license: 'MIT',
  },
  {
    name: '@trystero-p2p/ws-relay',
    what: 'credit.what.trysteroWsRelay',
    license: 'MIT',
  },
  {
    name: 'tone',
    what: 'credit.what.tone',
    license: 'MIT',
  },
  {
    name: 'immer',
    what: 'credit.what.immer',
    license: 'MIT',
  },
  {
    name: 'zod',
    what: 'credit.what.zod',
    license: 'MIT',
  },
  {
    name: 'ts-ascii-engine',
    what: 'credit.what.tsAsciiEngine',
    license: 'MIT',
  },
];

const BORROWED: readonly Credit[] = [
  {
    name: 'Alex Harri — WebGL Gradients',
    what: 'credit.what.alexharri',
    license: 'MIT · alexharri.com/blog/webgl-gradients',
  },
  {
    name: 'Ashima Arts / stegu — webgl-noise',
    what: 'credit.what.webglNoise',
    license: 'MIT · Copyright © 2011 Ashima Arts',
  },
  {
    name: 'Shadertoy "Glitch transform"',
    what: 'credit.what.glitch',
    license: 'CC BY-NC-SA 3.0 · www.shadertoy.com/view/XtyXzW',
  },
  {
    name: 'TETR.IO — zenith.rcd',
    what: 'credit.what.zenith',
    license: '℗ & © 2024—2025 Dr Ocelot · TETR.IO © 2019—2026 osk · tetr.io/res/bgm/zenith.rcd',
  },
];

export function createCreditScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-credit';
  let column = createColumn(1);

  const build = (): void => {
    const scale = ctx.settings.get().uiScale;
    const { width, height } = ctx.bounds;

    column = createColumn(scale);
    container.addChild(column.container);

    column.addRaw(pageChrome({ scale, width, height, header: bi('common.credit'), page: 'P.06' }));

    const sheet = panel({
      scale,
      width: PANEL_WIDTH,
      title: bi('common.credit'),
      padding: PANEL_PADDING,
    });
    sheet.container.position.set(Math.round((width - PANEL_WIDTH) / 2), uiPad(PANEL_TOP, scale));
    column.addRaw(sheet.container);

    const innerWidth = PANEL_WIDTH - uiPad(PANEL_PADDING * 2 + CONTENT_INSET, scale);
    // 从画布反推高度，而不是写死一个数再乘倍率（见 room.ts 的同名纪律）
    const overhead = uiPad(PANEL_TOP + 24 + PANEL_PADDING * 2 + BUTTON_RESERVE, scale);
    const area = scrollArea({
      scale,
      width: innerWidth,
      bleed: uiPad(CONTENT_INSET, scale),
      height: Math.max(uiPad(180, scale), height - overhead),
    });
    sheet.body.addChild(area.container);

    const inner = createColumn(scale, 10);
    area.content.addChild(inner.container);

    addSection(inner, scale, innerWidth, bi('credit.section.libraries'), LIBRARIES);
    addSection(inner, scale, innerWidth, bi('credit.section.borrowed'), BORROWED, 14);

    inner.add(
      richLabel({
        text: t('credit.note'),
        scale,
        size: 10,
        fill: INK.textDim,
        role: 'cjk',
        wrapWidth: innerWidth,
      }),
      0,
      uiPad(16, scale),
    );

    area.setContentHeight(Math.round(inner.y));
    sheet.fitContent(area.height);

    const backButton = stampButton({
      label: t('common.back'),
      scale,
      variant: 'primary',
      onPress: () => ctx.stack.pop(),
    });
    backButton.container.position.set(
      Math.round((width - backButton.width) / 2),
      height - uiPad(BUTTON_RESERVE - 8, scale),
    );
    column.addRaw(backButton.container);
  };

  build();

  return {
    container,
    rebuild() {
      column.dispose();
      container.removeChildren();
      build();
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      ctx.stack.pop();
      return true;
    },
    destroy() {
      column.dispose();
      container.destroy({ children: true });
    },
  };
}

/**
 * 一段鸣谢：一条分区标题 + 若干个条目。
 *
 * 每个条目是三行 —— **名字（正白）/ 是什么（次级） / 许可（机读小字）**。
 * 三行的墨是刻意递减的：先让眼睛抓到"这是谁"，再决定要不要读细节。
 */
function addSection(
  inner: ReturnType<typeof createColumn>,
  scale: number,
  width: number,
  title: string,
  entries: readonly Credit[],
  topGap = 0,
): void {
  inner.add(sectionTitle({ text: title, scale, width }), 0, uiPad(topGap, scale));
  for (const entry of entries) {
    inner.add(
      richLabel({
        text: entry.name,
        scale,
        size: 13,
        fill: INK.text,
        role: 'mono',
        bold: true,
        tracking: TRACKING.normal,
      }),
      0,
      uiPad(8, scale),
    );
    inner.add(
      richLabel({
        text: t(entry.what),
        scale,
        size: 11,
        fill: INK.textDim,
        role: 'cjk',
        wrapWidth: width,
      }),
      0,
      uiPad(2, scale),
    );
    inner.add(
      richLabel({
        text: entry.license,
        scale,
        size: 10,
        fill: INK.textDim,
        role: 'mono',
        tracking: TRACKING.wide,
      }),
      0,
      uiPad(2, scale),
    );
  }
}
